import React, { useState, useEffect } from "react";
import { Form, Switch, Select, Input, Icon, InputNumber, Tooltip, Alert, Button } from "antd";
import { isEmpty } from "lodash";
import { connect } from "react-redux";
import { updateCurrentUserAction } from "../../../redux/actions";
import { alertify, handleApiExceptions } from "../../../app_methods";
import { InstaCollectSettingsModel } from "../../../models";
import { Text2 } from "../../../styles";
import {
  calculateAutoTat,
  checkTimeIfGreaterThan,
  getInitialValues,
  PermissableSettingsButton,
  updateCurrentUserDetailsFn,
} from "./utils";
import { instaCollectTooltip, maxVals } from "./constants";
import { SplitAccountsDrawer } from "../../SmartCollect/splitAccountsDrawer";
import QRLogoConfigModal from "./qrLogoModal";
import { WebhookDrawer } from "../webhookDrawer";

const InstaCollectSettingsForm = (props) => {
  const { current_user, form, updateCurrentUser, handleFormFieldChange, noteObj } = props;
  const virtual_account_settings = current_user?.smart_collect_virtual_account_settings ?? {};
  const initialValues = getInitialValues(virtual_account_settings);

  const { getFieldDecorator, getFieldValue, setFieldsValue, validateFields } = form;
  const validations = InstaCollectSettingsModel.validations;

  const [isSaving, setIsSaving] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showWebhookDrawer, setShowWebhookDrawer] = useState(false);
  const [showConfigureQRLogoModal, setShowConfigureQRLogoModal] = useState(false);

  useEffect(() => {
    if (initialValues.enable_transaction_capture_flow) {
      handleFormFieldChange(props);
    }
  }, []);

  // Email_Enabled toggle change
  useEffect(() => {
    if (!getFieldValue("instacollect_email_enabled")) {
      validateFields(["instacollect_emails"]);
    }
  }, [getFieldValue("instacollect_email_enabled")]);

  //  SMS_Enabled toggle change
  useEffect(() => {
    if (!getFieldValue("instacollect_sms_enabled")) {
      validateFields(["instacollect_phone_number"]);
    }
  }, [getFieldValue("instacollect_sms_enabled")]);

  // Webhook toggle change
  useEffect(() => {
    if (!getFieldValue("webhook_enabled")) {
      validateFields(["webhook_url"]);

      if (getFieldValue("transaction_capture_flow_type_auto")) {
        setFieldsValue({
          transaction_capture_flow_type_auto: false,
        });
      }
    }
  }, [getFieldValue("webhook_enabled")]);

  // Capture_Flow_Toggle change
  useEffect(() => {
    if (!getFieldValue("enable_transaction_capture_flow")) {
      validateFields([
        "transaction_capture_tat",
        "transaction_capture_attempts",
        "transaction_capture_attempt_interval",
        "transaction_capture_attempt_interval_unit",
        "transaction_capture_flow_type_auto",
        "transaction_capture_flow_type_manual",
      ]);
    }
  }, [getFieldValue("enable_transaction_capture_flow")]);

  // Auto_Capture_Toggle change
  useEffect(() => {
    if (!getFieldValue("transaction_capture_flow_type_auto")) {
      validateFields([
        "transaction_capture_tat",
        "transaction_capture_attempts",
        "transaction_capture_attempt_interval",
        "transaction_capture_attempt_interval_unit",
        "transaction_capture_flow_type_manual",
      ]);
    } else {
      validateFields(["transaction_capture_flow_type_manual"]);
    }
  }, [getFieldValue("transaction_capture_flow_type_auto")]);

  // Manual_Capture_Toggle change
  useEffect(() => {
    validateFields(["transaction_capture_flow_type_auto"]);
  }, [getFieldValue("transaction_capture_flow_type_manual")]);

  // Auto_Capture_Max_Attempts, Attempt_Interval value change
  useEffect(() => {
    if (getFieldValue("transaction_capture_tat")) validateFields(["transaction_capture_tat"]);
  }, [getFieldValue("transaction_capture_attempts"), getFieldValue("transaction_capture_attempt_interval")]);

  // TATUnit value change
  useEffect(() => {
    validateFields(["transaction_capture_tat"]);
  }, [getFieldValue("transaction_capture_tat_unit")]);

  // AttemptIntervalUnit value change
  useEffect(() => {
    if (getFieldValue("transaction_capture_tat")) {
      validateFields(["transaction_capture_tat", "transaction_capture_attempt_interval"]);
    } else {
      validateFields(["transaction_capture_attempt_interval"]);
    }
  }, [getFieldValue("transaction_capture_attempt_interval_unit")]);

  const captureFlowValidator = (callback) => {
    if (
      getFieldValue("enable_transaction_capture_flow") &&
      !getFieldValue("transaction_capture_flow_type_auto") &&
      !getFieldValue("transaction_capture_flow_type_manual")
    ) {
      callback("At least one capture mode must be enabled.");
    } else {
      callback();
    }
  };

  const attemptIntervalValidator = (value, callback, required) => {
    if (!required || !value) return callback();

    // check for valid input
    const regex = /^[1-9][0-9]*$/;

    const unit = getFieldValue("transaction_capture_attempt_interval_unit");

    if (!regex.test(value)) {
      return callback("Invalid input");
    } else if (value > maxVals[unit]) {
      callback(`Max ${maxVals[unit]} ${unit} are allowed`);
    } else {
      callback();
    }
  };

  const autoRefundAfterValidator = (value, callback, required) => {
    if (!required || !value) return callback();

    // check for valid input
    const regex = /^[1-9][0-9]*$/;

    // check for max allowed values
    const autoRefundAfterUnit = getFieldValue("transaction_capture_tat_unit");
    maxVals.days = 12;

    const isGreaterThanAutoCaptureTime = checkTimeIfGreaterThan(
      {
        unit: autoRefundAfterUnit,
        value: getFieldValue("transaction_capture_tat"),
      },
      {
        unit: getFieldValue("transaction_capture_attempt_interval_unit"),
        value: getFieldValue("transaction_capture_attempt_interval"),
      },
      getFieldValue("transaction_capture_attempts")
    );

    if (!regex.test(value)) {
      return callback("Invalid input");
    } else if (value > maxVals[autoRefundAfterUnit]) {
      callback(`Max ${maxVals[autoRefundAfterUnit]} ${autoRefundAfterUnit} are allowed`);
    } else if (getFieldValue("transaction_capture_flow_type_auto") && !isGreaterThanAutoCaptureTime) {
      callback("Auto refund time must be more than Auto Capture time.");
    } else {
      callback();
    }
  };

  const TATUnit = (
    <>
      {getFieldDecorator("transaction_capture_tat_unit", {
        initialValue: initialValues.transaction_capture_tat_unit,
      })(
        <Select style={{ width: "90px" }} disabled={!getFieldValue("enable_transaction_capture_flow")}>
          <Select.Option value="hours">Hours</Select.Option>
          <Select.Option value="days">Days</Select.Option>
        </Select>
      )}
    </>
  );

  const AttemptIntervalUnit = (
    <>
      {getFieldDecorator("transaction_capture_attempt_interval_unit", {
        initialValue: initialValues.transaction_capture_attempt_interval_unit,
      })(
        <Select
          style={{ width: "90px" }}
          disabled={
            !getFieldValue("enable_transaction_capture_flow") ||
            !getFieldValue("transaction_capture_flow_type_auto") ||
            getFieldValue("transaction_capture_attempts") < 2 ||
            getFieldValue("transaction_capture_attempts") > 10
          }
        >
          <Select.Option value="minutes">Minutes</Select.Option>
          <Select.Option value="hours">Hours</Select.Option>
        </Select>
      )}
    </>
  );

  const onSubmitInstaCollect = (e) => {
    e.preventDefault();

    form.validateFields(async (err, values) => {
      if (!isEmpty(err)) return;

      values["instacollect_emails"] = values["instacollect_emails"];
      values["instacollect_phone_number"] = values["instacollect_phone_number"];

      values["webhook_url"] = values["webhook_enabled"] ? values["webhook_url"] : "";

      const transaction_capture_flow_type = [];
      if (values.transaction_capture_flow_type_auto) transaction_capture_flow_type.push("auto");
      if (values.transaction_capture_flow_type_manual) transaction_capture_flow_type.push("manual");
      values["transaction_capture_flow_type"] = transaction_capture_flow_type;

      setIsSaving(true);
      const response = await InstaCollectSettingsModel.update_settings(values);
      if (!response.success) {
        setIsSaving(false);
        handleApiExceptions(response);
        return;
      }
      alertify("Success", "Settings updated successfully.", "success");

      const updatedVirtualAccountSettings = updateCurrentUserDetailsFn(values, virtual_account_settings);

      updateCurrentUser({
        ...current_user,
        smart_collect_virtual_account_settings: updatedVirtualAccountSettings,
      });
      setIsSaving(false);
    });
  };

  const toggleConfigQRLogoModal = () => {
    setShowConfigureQRLogoModal(!showConfigureQRLogoModal);
  };

  const toggleWebhookConfigModal = () => {
    setShowWebhookDrawer(!showWebhookDrawer);
  };

  const configure_split_accounts = current_user?.split_settlement_type

  return (
    <div className="row">
      <div className="col-sm-12 col-md-12 col-lg-12">
        <Form onSubmit={onSubmitInstaCollect}>
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-12">
              <Text2 className=" mb-4 mt-2 d-block" strong>
                InstaCollect Notification
              </Text2>
              <div className="row">
                <div className="col-sm-6 col-md-2 col-lg-2">
                  <Form.Item label="Email Enabled">
                    {getFieldDecorator("instacollect_email_enabled", {
                      initialValue: initialValues.email_enabled,
                      valuePropName: "checked",
                    })(<Switch />)}
                  </Form.Item>
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6 instacollect-settings-col-inputs">
                  <Form.Item label="Emails">
                    {getFieldDecorator("instacollect_emails", {
                      initialValue: initialValues.instacollect_emails,
                      rules: validations.email(getFieldValue("instacollect_email_enabled")),
                    })(
                      <Select
                        mode="tags"
                        tokenSeparators={[","]}
                        disabled={!getFieldValue("instacollect_email_enabled")}
                      />
                    )}
                  </Form.Item>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6 col-md-2 col-lg-2">
                  <Form.Item label="SMS Enabled">
                    {getFieldDecorator("instacollect_sms_enabled", {
                      initialValue: initialValues.instacollect_sms_enabled,
                      valuePropName: "checked",
                    })(<Switch />)}
                  </Form.Item>
                </div>
                <div className="col-sm-6 col-md-6 col-lg-6 instacollect-settings-col-inputs">
                  <Form.Item label="Phone Numbers">
                    {getFieldDecorator("instacollect_phone_number", {
                      initialValue: initialValues.instacollect_phone_number,
                      rules: validations.phone(getFieldValue("instacollect_sms_enabled")),
                    })(
                      <Select
                        mode="tags"
                        tokenSeparators={[","]}
                        disabled={!getFieldValue("instacollect_sms_enabled")}
                      />
                    )}
                  </Form.Item>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {getFieldDecorator("id", {
              initialValue: virtual_account_settings.id,
            })(<Input hidden />)}
            <div className="col-sm-6 col-md-2 col-lg-2">
              <Form.Item label="Webhook Enabled">
                {getFieldDecorator("webhook_enabled", {
                  initialValue: initialValues.webhook_enabled,
                  valuePropName: "checked",
                })(<Switch />)}
              </Form.Item>
            </div>
            <div className="col-sm-6 col-md-6 col-lg-6 instacollect-settings-col-inputs">
              {/* <Form.Item label="Webhook URL">
                {getFieldDecorator("webhook_url", {
                  initialValue: initialValues.webhook_url,
                  rules: validations.webhook_url(getFieldValue("webhook_enabled")),
                })(
                  <Input
                    autoComplete="off"
                    prefix={<Icon type="edit" />}
                    disabled={!getFieldValue("webhook_enabled")}
                  />
                )}
              </Form.Item> */}
              <Button
                className="p-0 mt-3"
                type="link"
                onClick={() => {
                  setShowWebhookDrawer(true);
                }}
                disabled={!getFieldValue("webhook_enabled")}
              >
                Configure Webhook
              </Button>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-3 col-md-2 col-lg-2">
              <Form.Item
                label={
                  <>
                    <span>Capture Flow</span>
                    <Tooltip
                      title={instaCollectTooltip.captureFlowTooltip}
                      className="mr-1 instacollect-settings-tooltip"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Icon style={{ fontSize: 14, marginTop: "3px" }} className="ml-1" type="info-circle" />
                    </Tooltip>
                  </>
                }
              >
                {getFieldDecorator("enable_transaction_capture_flow", {
                  initialValue: initialValues.enable_transaction_capture_flow,
                  valuePropName: "checked",
                })(<Switch />)}
              </Form.Item>
            </div>

            <div className="col-md-10 col-lg-10">
              <div className="row instacollect-settings-autocapture">
                <div className="col-md-3 col-lg-3 d-flex">
                  <Form.Item
                    label={
                      <>
                        <span>Auto Capture</span>
                        <Tooltip
                          title={instaCollectTooltip.autoCaptureTooltip}
                          className="ml-2 instacollect-settings-tooltip"
                          onClick={(e) => e.preventDefault()}
                        >
                          <Icon style={{ fontSize: 14, marginTop: "3px" }} className="ml-1 mr-1" type="info-circle" />
                        </Tooltip>
                      </>
                    }
                  >
                    {getFieldDecorator("transaction_capture_flow_type_auto", {
                      initialValue: initialValues.transaction_capture_flow_type.includes("auto"),
                      valuePropName: "checked",
                      rules: [
                        {
                          validator: (_rule, _values, callback) => captureFlowValidator(callback),
                        },
                      ],
                    })(
                      <Switch
                        disabled={
                          !getFieldValue("enable_transaction_capture_flow") || !getFieldValue("webhook_enabled")
                        }
                      />
                    )}
                  </Form.Item>
                </div>
                <div className="col-md-8 col-lg-8 pl-0 d-flex">
                  <Form.Item label="Auto Capture Max Attempts">
                    {getFieldDecorator("transaction_capture_attempts", {
                      initialValue: initialValues.transaction_capture_attempts,
                      rules: validations.transaction_capture_attempts(
                        getFieldValue("enable_transaction_capture_flow") &&
                          getFieldValue("transaction_capture_flow_type_auto")
                      ),
                    })(
                      <Input
                        style={{ width: "293px" }}
                        autoComplete="off"
                        disabled={
                          !getFieldValue("enable_transaction_capture_flow") ||
                          !getFieldValue("transaction_capture_flow_type_auto")
                        }
                      />
                    )}
                  </Form.Item>
                  <Form.Item label="Attempt Interval" className="ml-4">
                    {getFieldDecorator("transaction_capture_attempt_interval", {
                      initialValue: initialValues.transaction_capture_attempt_interval,
                      rules: [
                        {
                          required:
                            getFieldValue("enable_transaction_capture_flow") &&
                            getFieldValue("transaction_capture_flow_type_auto"),
                          message: "Attempt interval required.",
                        },
                        {
                          validator: (_rule, values, callback) =>
                            attemptIntervalValidator(
                              values,
                              callback,
                              getFieldValue("enable_transaction_capture_flow") &&
                                getFieldValue("transaction_capture_flow_type_auto")
                            ),
                        },
                      ],
                    })(
                      <Input
                        style={{ width: "100%" }}
                        autoComplete="off"
                        disabled={
                          !getFieldValue("enable_transaction_capture_flow") ||
                          !getFieldValue("transaction_capture_flow_type_auto") ||
                          getFieldValue("transaction_capture_attempts") < 2 ||
                          getFieldValue("transaction_capture_attempts") > 10
                        }
                        addonAfter={AttemptIntervalUnit}
                      />
                    )}
                  </Form.Item>
                  <Form.Item
                    className="ml-4"
                    label={
                      <>
                        <span>Auto Capture TAT</span>
                        <Tooltip
                          title={instaCollectTooltip.autoCaptureTAT}
                          className="mr-1 instacollect-settings-tooltip"
                          onClick={(e) => e.preventDefault()}
                        >
                          <Icon style={{ fontSize: 14, marginTop: "3px" }} className="ml-1" type="info-circle" />
                        </Tooltip>
                      </>
                    }
                  >
                    <p>
                      {getFieldValue("enable_transaction_capture_flow") &&
                      getFieldValue("transaction_capture_flow_type_auto")
                        ? calculateAutoTat(
                            getFieldValue("transaction_capture_attempts"),
                            getFieldValue("transaction_capture_attempt_interval"),
                            getFieldValue("transaction_capture_attempt_interval_unit")
                          )
                        : "---"}
                    </p>
                  </Form.Item>
                </div>
              </div>

              <div className="row instacollect-settings-manualcapture">
                <div className="col-md-3 col-lg-3">
                  <Form.Item
                    label={
                      <>
                        <span>Manual Capture</span>
                        <Tooltip
                          title={instaCollectTooltip.manualCaptureTooltip}
                          className="ml-2 instacollect-settings-tooltip"
                          onClick={(e) => e.preventDefault()}
                        >
                          <Icon style={{ fontSize: 14, marginTop: "3px" }} className="ml-1 mr-1" type="info-circle" />
                        </Tooltip>
                      </>
                    }
                  >
                    {getFieldDecorator("transaction_capture_flow_type_manual", {
                      initialValue: initialValues.transaction_capture_flow_type.includes("manual"),
                      valuePropName: "checked",
                      rules: [
                        {
                          validator: (_rule, _values, callback) => captureFlowValidator(callback),
                        },
                      ],
                    })(<Switch disabled={!getFieldValue("enable_transaction_capture_flow")} />)}
                  </Form.Item>
                </div>
                <div className="col-md-8 col-lg-8 pl-0">
                  {getFieldValue("enable_transaction_capture_flow") && (
                    <div className="instacollect-settings-manualcapture-info">
                      <div>{noteObj.manualNote}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-3 col-lg-3 d-flex">
                  <div className="instacollect-settings-refunddiv w-75">
                    <Form.Item label="Auto Refund After" className="instacollect-settings-refunddiv-input">
                      {getFieldDecorator("transaction_capture_tat", {
                        initialValue: initialValues.transaction_capture_tat,
                        rules: [
                          {
                            required: getFieldValue("enable_transaction_capture_flow"),
                            message: "Auto refund after required.",
                          },
                          {
                            validator: (_rule, values, callback) =>
                              autoRefundAfterValidator(
                                values,
                                callback,
                                getFieldValue("enable_transaction_capture_flow")
                              ),
                          },
                        ],
                      })(
                        <Input
                          autoComplete="off"
                          disabled={!getFieldValue("enable_transaction_capture_flow")}
                          addonAfter={TATUnit}
                        />
                      )}
                    </Form.Item>
                  </div>
                </div>
                <div className="col-md-8 col-lg-8 pl-0">
                  {getFieldValue("enable_transaction_capture_flow") && (
                    <p className="instacollect-settings-refund-info">{noteObj.autoRefundNote}</p>
                  )}
                </div>
              </div>
              <div className="pl-0 instacollect-settings-footnote">
                {getFieldValue("enable_transaction_capture_flow") && (
                  <Alert
                    message={
                      <span>
                        <strong>Note</strong> : {instaCollectTooltip.footNote}
                      </span>
                    }
                    type="info"
                  />
                )}
              </div>
            </div>
          </div>

          {current_user?.ess && current_user?.esc  ? (
            <div className="row">
              <div className="instacollect-settings-split-account col-sm-6 col-md-2 col-lg-2">Split Account:</div>
              <div className="col-sm-6 col-md-2 col-lg-2">
                <Button
                  className="p-0"
                  type="link"
                  onClick={() => {
                    setShowDrawer(true);
                  }}
                >
                  Configure Split Accounts
                </Button>
              </div>
            </div>
          ) : null}

          <div className="row">
            <div className="instacollect-settings-split-account col-sm-6 col-md-2 col-lg-2">UPI QR Logo:</div>
            <div className="col-sm-6 col-md-2 col-lg-2">
              <Button
                className="p-0"
                type="link"
                onClick={() => {
                  setShowConfigureQRLogoModal(true);
                }}
              >
                Configure
              </Button>
            </div>
          </div>

          <div className="w-100 d-flex justify-content-end align-self-end mt-2">
            <PermissableSettingsButton type="primary" htmlType="submit" loading={isSaving}>
              Save
            </PermissableSettingsButton>
          </div>
        </Form>
        <SplitAccountsDrawer
          width="46%"
          visible={showDrawer}
          onClose={() => setShowDrawer(false)}
          merchantId={current_user?.id}
          split_settlement_type={current_user?.split_settlement_type}
          cva_id={""}
          can_edit={current_user?.split_settlement_type=="MVA"}

        />
        <QRLogoConfigModal
          current_user={current_user}
          updateCurrentUser={updateCurrentUser}
          width={350}
          title={false}
          visible={showConfigureQRLogoModal}
          toggleConfigQRLogoModal={() => toggleConfigQRLogoModal()}
          centered
          hideFooter
          destroyOnClose
        />
        <WebhookDrawer width="70%" visible={showWebhookDrawer} toggleWebhookConfigModal={() =>toggleWebhookConfigModal()} isInsta={showWebhookDrawer}/>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  current_user: state.userReducer.current_user,
});

const mapDispatchToProps = (dispatch) => ({
  updateCurrentUser: (user) => dispatch(updateCurrentUserAction(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InstaCollectSettingsForm);
